import {Concept} from 'types';
import type {Schemas} from 'api/dto.generated';
import {ebsApi} from 'store/ebsApi';
import type {
  AvailableFilter,
  Column,
  ColumnSet,
  DataResponse,
  FilterSet,
  PostInfoArgs,
  ViewMode,
} from './types';
import {triggerDownloadBlob} from '../../store/api/triggerDownloadBlob';
import {sortColumnSet} from './functions';
import {mapAvailableFilters, mapToFilters} from './mappers';
import {mapAvailableColumn} from './mappers/mapAvailableColumn';
import {mapFilterExportResponse} from './mappers/mapFilterExportResponse';
import {mapPostInfoApiArgs} from './mappers/mapPostInfoApiArgs';
import {mapSavedFilters} from './mappers/mapSavedFilters';
import {mapViewMode} from './mappers/mapViewMode';

const advancedSearchApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getAvailableColumns: build.query<Column[], ViewMode, Schemas.Column[]>({
      query: type => ({
        url: `advancedsearch/columns`,
        params: {
          type: mapViewMode(type),
        },
      }),
      transformResponse: (response: Schemas.Column[], _, type) =>
        response.map(r => mapAvailableColumn(r, type === Concept.work)),
    }),

    getAvailableFilters: build.query<
      AvailableFilter[],
      void,
      Schemas.AvailableFilter[]
    >({
      keepUnusedDataFor: 60 * 2,
      query: () => `advancedsearch/filters`,
      transformResponse: mapAvailableFilters,
    }),

    getFilterSets: build.query<
      FilterSet[],
      void,
      Schemas.GetFilterSetsResponse
    >({
      keepUnusedDataFor: 60 * 2,
      query: () => `advancedsearch/filters/set`,
      transformResponse: response => mapSavedFilters(response.filters ?? []),
      providesTags: (result = []) =>
        result.map(({id}) => ({type: 'FilterSet' as const, id})),
    }),

    createFilterSet: build.mutation<boolean, Omit<FilterSet, 'id'>>({
      query: ({name, filters, isPublic, columns}) => ({
        url: `advancedsearch/filters/set`,
        method: 'POST',
        body: {
          name,
          filters: mapToFilters(filters, true),
          isPublic,
          columns,
        },
      }),
      transformResponse: (): boolean => {
        return true;
      },
      transformErrorResponse: (): boolean => {
        return false;
      },
      invalidatesTags: ['FilterSet'],
    }),

    updateFilterSet: build.mutation<FilterSet, FilterSet>({
      query: ({id, name, filters, isPublic, columns}) => ({
        url: `advancedsearch/filters/set`,
        method: 'PUT',
        body: {
          id,
          name,
          filters: mapToFilters(filters, true),
          isPublic,
          columns,
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: 'FilterSet' as const, id}],
    }),

    deleteFilterSet: build.mutation<void, Pick<FilterSet, 'id'>>({
      query: ({id}) => ({
        url: `advancedsearch/filters/set`,
        method: 'DELETE',
        body: `"${id}"`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: 'FilterSet' as const, id}],
    }),

    getColumnSets: build.query<
      ColumnSet[],
      void,
      Schemas.GetColumnSetsResponse
    >({
      keepUnusedDataFor: 60 * 2,
      query: () => `advancedsearch/columns/set`,
      transformResponse: response =>
        (response.columns ?? []).sort(sortColumnSet),
      providesTags: (result = []) =>
        result.map(({id}) => ({type: 'ColumnSet' as const, id})),
    }),

    createColumnSet: build.mutation<ColumnSet, Omit<ColumnSet, 'id'>>({
      query: ({name, columns, isPublic}) => ({
        url: `advancedsearch/columns/set`,
        method: 'POST',
        body: {
          name,
          columns,
          isPublic,
        },
      }),
      invalidatesTags: ['ColumnSet'],
    }),

    updateColumnSet: build.mutation<ColumnSet, ColumnSet>({
      query: ({id, name, columns, isPublic}) => ({
        url: `advancedsearch/columns/set`,
        method: 'PUT',
        body: {
          id,
          name,
          columns,
          isPublic,
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: 'ColumnSet' as const, id}],
    }),

    deleteColumnSet: build.mutation<void, Pick<ColumnSet, 'id'>>({
      query: ({id}) => ({
        url: `advancedsearch/columns/set`,
        method: 'DELETE',
        body: `"${id}"`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: 'ColumnSet' as const, id}],
    }),

    postInfo: build.query<
      DataResponse,
      PostInfoArgs,
      Schemas.ExportInformationResponse
    >({
      query: args => {
        const {eans, workIds, type, filters, columns, page, size, sort} =
          mapPostInfoApiArgs(args);
        return {
          url: `advancedsearch/info`,
          method: 'POST',
          params: {
            page: page,
            size: size,
            sort: sort,
          },
          body: {
            eans,
            workIds,
            type,
            filters,
            columns,
          },
        };
      },
      transformResponse: mapFilterExportResponse,
    }),

    postExport: build.query<boolean, PostInfoArgs>({
      // Custom handling to prevent download from being cached by RTK Query.
      // Also we trigger download of Blob instead of returning content response.
      queryFn: async (args, _queryApi, _extraOptions, fetchWithBQ) => {
        const {eans, workIds, type, filters, distinctOn, columns} =
          mapPostInfoApiArgs(args);

        const response = await fetchWithBQ({
          url: `advancedsearch/export`,
          method: 'POST',
          body: {
            eans,
            workIds,
            type,
            filters,
            columns,
            distinctOn,
          },
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreatsheetml.sheet',
          },
          responseHandler: response => {
            if (response.status !== 200) {
              return response.json();
            }
            return response.blob().then(async blob => {
              triggerDownloadBlob(
                `ebba-export-${new Date().toLocaleString('no')}.xlsx`,
                blob,
              );
              return {data: true};
            });
          },
        });

        if (response.error) {
          return {error: response.error, meta: response.meta};
        }

        return {data: true, error: undefined, meta: response.meta};
      },
    }),
  }),
});

export const {
  useLazyPostInfoQuery,
  useGetAvailableColumnsQuery,
  useGetAvailableFiltersQuery,
  useLazyPostExportQuery,
  useGetFilterSetsQuery,
  useCreateFilterSetMutation,
  useUpdateFilterSetMutation,
  useDeleteFilterSetMutation,
  useGetColumnSetsQuery,
  useCreateColumnSetMutation,
  useUpdateColumnSetMutation,
  useDeleteColumnSetMutation,
} = advancedSearchApi;
